import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Quotes } from "@phosphor-icons/react";
import { useLang } from "../i18n/LangContext";
import { EditorialNumber } from "./EditorialNumber";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const revealUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.85, ease: [0.7, 0, 0.2, 1] },
  }),
};

export const Testimonials = () => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then((res) => setItems(res.data)).catch(() => setItems([]));
  }, []);

  // Pick the localized field with graceful fallback: requested lang → IT → EN.
  const localised = (obj, field) => obj?.[`${field}_${lang}`] || obj?.[`${field}_it`] || obj?.[`${field}_en`] || "";

  const featured = items[0];
  const others = items.slice(1);

  return (
    <section
      id="testimonials"
      className="relative bg-[#2E2A26] text-[#F1EEE8] py-28 md:py-40 overflow-hidden"
      data-testid="testimonials-section"
    >
      {/* Ambient background — sober elegance, warm reading nook (trust & tradition) */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=2400&q=90"
          alt=""
          className="w-full h-full object-cover object-center bg-ken-burns"
          style={{ filter: "contrast(1.02) brightness(0.72) saturate(0.88)" }}
        />
        {/* Sober dark overlay — image is a discreet ambient presence */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(30,26,22,0.78) 0%, rgba(24,20,16,0.84) 55%, rgba(15,12,10,0.90) 100%)",
          }}
        />
        {/* Subtle brass hairline glow bottom-right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 80% 80%, rgba(184,150,97,0.08) 0%, rgba(184,150,97,0.0) 45%)",
          }}
        />
      </div>

      <EditorialNumber n="05" variant="light" align="right" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-3">
            <div className="flex items-center gap-3 text-[#F1EEE8]/55 text-[0.7rem] tracking-[0.28em] uppercase">
              <span className="w-6 h-px bg-[#13535A]" />
              <span>{t.testimonials.overline}</span>
            </div>
          </div>
          <h2 className="md:col-span-9 font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.2rem,5.4vw,4.8rem)] whitespace-pre-line">
            {t.testimonials.title}
          </h2>
        </div>

        {/* Featured */}
        {featured && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={revealUp}
            className="grid md:grid-cols-12 gap-10 md:gap-14 items-start mb-24 md:mb-32 border-b border-[#F1EEE8]/12 pb-16 md:pb-24"
          >
            <div className="md:col-span-1 hidden md:flex text-[#B08F5C]/60">
              <Quotes size={64} weight="fill" />
            </div>
            <div className="md:col-span-11">
              <p className="font-display italic-serif text-3xl md:text-5xl leading-[1.14] tracking-tight max-w-5xl mb-10">
                &ldquo;{localised(featured, "quote")}&rdquo;
              </p>
              <div className="flex items-baseline gap-6">
                <div className="tracking-tight text-xl" style={{fontFamily:"Inter", fontWeight:500}}>
                  {featured.author}
                </div>
                <div className="text-[#F1EEE8]/55 text-sm">
                  {localised(featured, "role")}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Grid of remaining */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {others.map((r, i) => (
            <motion.div
              key={r.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              variants={revealUp}
              custom={i}
              className="relative border border-[#F1EEE8]/12 hover:border-[#B08F5C]/50 transition-colors p-8 md:p-10 flex flex-col justify-between min-h-[280px]"
              data-testid={`testimonial-card-${r.id}`}
            >
              <Quotes size={28} weight="light" className="text-[#B08F5C]/60 mb-6" />
              <p className="font-body text-[#F1EEE8]/85 leading-relaxed text-[1rem] mb-6 flex-1">
                &ldquo;{localised(r, "quote")}&rdquo;
              </p>
              <div className="pt-4 border-t border-[#F1EEE8]/12">
                <div className="tracking-tight text-base" style={{fontFamily:"Inter", fontWeight:500}}>
                  {r.author}
                </div>
                <div className="text-[#F1EEE8]/50 text-[0.82rem] mt-1">
                  {localised(r, "role")}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
